/**
 * The room conversation protocol — who owes a reply to what (#1107).
 *
 * WHY THIS IS ITS OWN MODULE. The rule used to live inside `openclaw-entry.ts`,
 * the OpenClaw plugin's entry point. Only wren and cortina run through it. The
 * three native bridges — grok (ohtani), claude (loopita) and muse (wowzer,
 * muse) — build a `SecureChannel` directly and decided replies with an entirely
 * separate rule of their own:
 *
 *     replyExpected: e.senderIsAgent === false     // "answer any human"
 *
 * So #1107 shipped to two of THREE implementations, and four of nine production
 * agents would have kept answering every un-mentioned message — the cascade,
 * half-fixed, in the half most likely to be read as "the protocol doesn't work".
 *
 * #1107 itself said the rule "lives in hermes channel/mention.py and the
 * OpenClaw plugin openclaw-entry.ts. Any change has to land in both." It named
 * two places. There were three. That is #1012/#1014/#1016 again: a rule at N
 * call sites decays to a rule at N-1, and a hand-written enumeration is always
 * missing one.
 *
 * So the rule lives here, is exported from the package root, and every
 * TypeScript client imports it. The only remaining twin is hermes'
 * `agentvault_hermes/channel/mention.py` — Python and TypeScript cannot share a
 * module — and that one is pinned by a cross-language test.
 *
 * THE PROTOCOL
 *
 *     no @              -> NOBODY replies   (speak IN the room, not TO anyone)
 *     @wren / @a @b     -> those named
 *     @all / @everyone  -> everyone, HUMANS ONLY
 *
 * Everyone still RECEIVES and READS all of it. The @ decides who owes an
 * answer, never who is allowed to see.
 */

/** The mention token: word characters AND hyphens (#1128).
 *
 * It was `\w+` only, which truncated `@hermes-test` to `hermes` and left a
 * hyphenated agent structurally unreachable once #1107 made the `@` the answer
 * key. `.` is still excluded, so `user@example.com` -> `["example"]` holds.
 *
 * Kept byte-identical to hermes' `_MENTION_RE` in
 * `packages/plugin-hermes/src/agentvault_hermes/channel/mention.py`.
 */
const MENTION_RE = /@([\w-]+)/gi;

const BROADCAST_NAMES = new Set(["all", "everyone"]);

/** Extract @mention names from plaintext. Returns lowercased names. */
export function parseMentions(text: string): string[] {
  const mentions: string[] = [];
  // Fresh regex per call — a module-level /g regex carries `lastIndex` between
  // calls and would skip matches on the second invocation.
  const re = new RegExp(MENTION_RE.source, MENTION_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    mentions.push(match[1].toLowerCase());
  }
  return mentions;
}

/** Which member of `names` does this mention token address? `null` if none.
 *
 * #1128 — a widened token is a CANDIDATE, not a name. Exact match first, then
 * progressively shorter hyphen-prefixes, longest first:
 *
 *     "hermes-test"     vs {"hermes-test"}  -> "hermes-test"  (a name)
 *     "wren-please-look" vs {"wren"}        -> "wren"         (a name + prose)
 *     "hermes-test"     vs {"cortina"}      -> null
 *
 * The MATCHED PREFIX is returned so `stripMentions` can remove exactly that
 * much and leave the remainder — which is what the pre-#1128 parser did.
 */
function resolveOwn(mention: string, names: Set<string>): string | null {
  if (names.has(mention)) return mention;
  const parts = mention.split("-");
  for (let i = parts.length - 1; i > 0; i--) {
    const candidate = parts.slice(0, i).join("-");
    if (names.has(candidate)) return candidate;
  }
  return null;
}

/** Does this agent owe a reply to this room message? (#1107)
 *
 * NOT a delivery decision — `channel.ts` records every inbound room message to
 * history before this is reached, and that history is replayed as context on
 * every turn the agent does take. A `false` here costs the agent the reply,
 * never the room.
 *
 * The rate limiter (`_roomCanReply`: 4 replies/60s, 75s cooldown) is a separate
 * mechanism and a safety net: it bounds how OFTEN an agent may answer; this
 * bounds WHETHER it owes an answer at all. Before #1107 the limiter was the
 * only brake, which is why one un-mentioned "good morning" cost 18 replies.
 */
export function owesRoomReply(
  plaintext: string,
  agentName: string,
  accountId: string,
  senderIsAgent?: boolean,
  roomDisplayName?: string,
  ownDisplayName?: string,
  selfResolved: boolean = true,
): boolean {
  const mentions = parseMentions(plaintext);
  // #1107 — @ IS THE ANSWER KEY, NOT THE DELIVERY KEY.
  //
  // This decides only whether THIS agent owes a reply. It no longer decides
  // whether the agent SEES the message: `channel.ts` appends every inbound room
  // message to history BEFORE invoking this handler, and that history is
  // injected into the LLM context as `[Recent room conversation]` on every turn
  // the agent does take. So an agent that stays quiet here still reads the room
  // and can answer about it later — which is the property the old comment
  // ("gating here made agents blind") was protecting, by a mechanism that no
  // longer requires answering everything.
  //
  // No @ at all → NOBODY owes a reply. This is the cascade brake.
  // Measured 2026-08-29, room 007: one un-mentioned "good morning" from the
  // owner produced 18 agent replies in 115 seconds, 6 of them in a minute where
  // no human spoke at all. Every reply was itself un-mentioned, so every reply
  // re-broadcast. A bare message is now how you speak IN the room without
  // speaking TO anyone.
  if (mentions.length === 0) return false;
  // Check if this agent is mentioned by framework name/accountId, its AgentVault
  // room display_name, OR its hub-identity display_name (what others actually
  // address — the framework agentName is often the generic "default").
  // BUG-20260604-002 + regression 2026-07-08.
  //
  // #1128 — this runs BEFORE the broadcast check. An agent literally named
  // "all-hands" is then ADDRESSED rather than broadcast to, which is the only
  // way it stays reachable from an agent sender.
  const ownNames = ownRoomNames(agentName, accountId, roomDisplayName, ownDisplayName);
  if (mentions.some((m) => resolveOwn(m, ownNames) !== null)) return true;
  // @all / @everyone → everyone answers, but HUMANS ONLY. An agent that could
  // broadcast would re-open the cascade through the one door left. Agents
  // address people and each other by name; a room-wide question goes through a
  // person (Chris, 2026-08-30).
  if (mentions.some((m) => resolveOwn(m, BROADCAST_NAMES) !== null)) {
    return senderIsAgent !== true;
  }
  // Fail-open (regression 2026-07-08, live rooms "room"/bodega): if this agent's
  // own identity could NOT be resolved (no hub display_name, no room roster entry,
  // and the generic framework name), do NOT silently drop an @-addressed message.
  // Going deaf to your own name is far worse than occasionally processing a
  // message meant for a sibling — and the rate-limiter bounds any loop. Only skip
  // an unmatched @mention when we actually KNOW our own names.
  if (!selfResolved) return true;
  // Has @mentions, none match this agent, and we know who we are — we are not
  // on the hook. We still read it (see the history note above).
  return false;
}

/** Lower-cased set of names that count as "this agent" in a room: framework
 * name + first word + accountId, plus the AgentVault room display_name (+ its
 * first word) when known. Shared by owesRoomReply + stripMentions. */
export function ownRoomNames(
  agentName: string,
  accountId: string,
  roomDisplayName?: string,
  ownDisplayName?: string,
): Set<string> {
  const names = new Set<string>();
  const add = (n: string | undefined) => {
    if (!n) return;
    const lower = n.toLowerCase();
    names.add(lower);
    names.add(lower.split(/[\s(]/)[0]);
  };
  add(agentName);
  add(accountId);
  add(roomDisplayName);
  // Hub-identity display_name (channel.ownDisplayName): resolved from the agent's
  // OWN identity at connect, independent of any specific room's roster — so the
  // agent still knows its own name when a room roster is incomplete/degraded
  // (regression 2026-07-08: selfName collapsed to "default" → went deaf to @name).
  add(ownDisplayName);
  return names;
}

/** Strip the matching @mention prefix from plaintext so the agent sees clean text. */
export function stripMentions(
  text: string,
  agentName: string,
  accountId: string,
  roomDisplayName?: string,
  ownDisplayName?: string,
): string {
  const ownNames = ownRoomNames(agentName, accountId, roomDisplayName, ownDisplayName);
  // Remove all @mentions that match this agent (case-insensitive), plus
  // @all/@everyone since they are routing directives.
  return text
    .replace(new RegExp(MENTION_RE.source, MENTION_RE.flags), (full, name: string) => {
      const lower = name.toLowerCase();
      const matched = resolveOwn(lower, ownNames) ?? resolveOwn(lower, BROADCAST_NAMES);
      if (matched === null) return full;
      // #1128 — remove "@" + the MATCHED PREFIX only. When the whole token is
      // our name ("@hermes-test") that is all of it; when it is our name
      // followed by prose ("@wren-please-look") the remainder is text the
      // author wrote, and the pre-#1128 parser left it in place too.
      return name.slice(matched.length);
    })
    .replace(/^\s+/, "") // trim leading whitespace left by stripped mention
    .trim();
}

/**
 * #1239 — strip mentions from the CAPTION only, never from the fold.
 *
 * channel.ts prepends every fold (attachment banner + file contents, the
 * #1181 "most recent file" note) and then the `[sender]: ` prefix, so the
 * caption the sender typed is the SUFFIX of `text`; `caption` is that same
 * string, carried separately on the emit since #1223. Only that occurrence is
 * de-@'d — a pasted file that says "@wren" reaches the model exactly as sent.
 * (An empty caption therefore falls through unchanged: nothing typed, nothing
 * to strip.)
 *
 * Located with `lastIndexOf`, not `endsWith`, so a future APPEND after the
 * caption still finds it — hermes already appends its file note, and a port
 * that assumed "suffix" would fall back on every message there.
 *
 * Fallbacks, both deliberate:
 *   - `caption` not a string: the whole-text strip this replaced. Defensive
 *     only — channel.ts and this module ship in ONE bundle (each bridge bundles
 *     the plugin at build time too), so a room message without `caption` cannot
 *     come from a version skew, only from a caller of the handler that never
 *     set it.
 *   - `caption` a string but NOT IN `text`: the shape assumption no longer
 *     holds. Return `text` UNCHANGED and say so (with the room). Failing toward
 *     "the model sees @wren" is harmless; failing toward "the pasted file was
 *     edited" is the #1239 incident itself.
 */
export function stripCaptionMentions(
  text: string,
  caption: string | undefined,
  agentName: string,
  accountId: string,
  roomDisplayName?: string,
  ownDisplayName?: string,
  roomId?: string,
): string {
  if (typeof caption !== "string") {
    return stripMentions(text, agentName, accountId, roomDisplayName, ownDisplayName);
  }
  const at = text.lastIndexOf(caption);
  if (at < 0) {
    console.warn(
      `[AgentVault] caption not found in the folded text — left un-stripped rather than edit the fold (#1239) ` +
        `room=${(roomId ?? "").slice(0, 8)} caption_len=${caption.length} text_len=${text.length}`,
    );
    return text;
  }
  return (
    text.slice(0, at) +
    stripMentions(caption, agentName, accountId, roomDisplayName, ownDisplayName) +
    text.slice(at + caption.length)
  );
}
